/**
 * RevenueMetrics
 *
 * Revenue & Financial Metrics section of the analytics dashboard.
 * Displays MRR, ARR, Paying Customers, and ARPU metrics.
 */
import React from 'react';
import { DollarSign, Users, Activity, BarChart3 } from 'lucide-react';
import AnalyticsTooltip from './AnalyticsTooltip';

const RevenueMetrics = ({ analyticsData, formatCurrency }) => {
  return (
    <div className="mb-8">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Revenue & Financial Metrics</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
        {/* Current MRR */}
        <AnalyticsTooltip content="Monthly Recurring Revenue - Total predictable revenue generated each month from active subscriptions. Includes both monthly and annual plans (annual converted to monthly equivalent)." id="mrr-tooltip">
          <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg mr-3">
                <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-600 dark:text-gray-400">Monthly Recurring Revenue</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(analyticsData.summary?.currentMRR || 0)}
                </p>
                <p className="text-xs text-green-600 dark:text-green-400">
                  {analyticsData.summary?.mrrGrowthRate > 0 ? '+' : ''}{analyticsData.summary?.mrrGrowthRate?.toFixed(1) || 0}% growth
                </p>
              </div>
            </div>
          </div>
        </AnalyticsTooltip>

        {/* ARR (Annual Recurring Revenue) */}
        <AnalyticsTooltip content="Annual Recurring Revenue (ARR) - Estimated yearly revenue based on current MRR. Calculated as MRR × 12. This shows the annualized value of your recurring revenue and is more predictive for SaaS businesses." id="arr-tooltip">
          <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg mr-3">
                <Activity className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-600 dark:text-gray-400">ARR (Estimated)</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency((analyticsData.summary?.currentMRR || 0) * 12)}
                </p>
                <p className="text-xs text-yellow-600 dark:text-yellow-400">
                  Annual run rate
                </p>
              </div>
            </div>
          </div>
        </AnalyticsTooltip>

        {/* Total Paying Customers */}
        <AnalyticsTooltip content="Paying Customers - Number of users with active subscriptions (subscriptionStatus = 'active'). These customers are currently generating revenue and contributing to MRR." id="paying-customers-tooltip">
          <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg mr-3">
                <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-600 dark:text-gray-400">Paying Customers</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {(analyticsData.summary?.payingCustomers || 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  of {(analyticsData.summary?.totalUsers || 0).toLocaleString()} total users
                </p>
              </div>
            </div>
          </div>
        </AnalyticsTooltip>

        {/* ARPU */}
        <AnalyticsTooltip content="ARPU (Average Revenue Per User) - Average monthly revenue per paying customer. Calculated as MRR ÷ Number of Paying Customers. Shows how much monthly revenue each customer generates on average." id="arpu-tooltip">
          <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/20 rounded-lg mr-3">
                <BarChart3 className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-600 dark:text-gray-400">ARPU</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(analyticsData.summary?.averageRevenuePerUser || 0)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">per paying customer</p>
              </div>
            </div>
          </div>
        </AnalyticsTooltip>
      </div>
    </div>
  );
};

export default RevenueMetrics;


